import React, { useEffect, useMemo, useRef, useState } from "react";
import HRNavbar from "../../../HR/HRNavbar";
import Footer from "../../../../Footer";
import {
  CircularProgress,
  TextField,
  Button,
  InputBase,
  MenuItem,
  IconButton,
  Typography,
  TablePagination,
  CardContent,
  Card,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  useMediaQuery,
} from "@mui/material";
import { Box, Stack, styled } from "@mui/system";
import DatePicker from "react-multi-date-picker";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import { API_URL } from "../../../Config/api";

import LongTermReportModal from "./LongTermReportModal";

const LongtermFormcardHader = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "white",
  height: "45px",
  borderRadius: "8px",
  boxShadow: "0px 1px 5px rgba(0, 0, 0, 0.03)", // ⬅ softer shadow
  margin: "4px 0", // ⬅ reduce vertical spacing
  padding: "0 8px",
  transition: "all 0.3s ease",
}));
const LongtermFormcard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "white",
  height: "45px",
  borderRadius: "8px",
  boxShadow: "0px 1px 5px rgba(0, 0, 0, 0.03)", // ⬅ softer shadow
  margin: "4px 0", // ⬅ reduce vertical spacing
  padding: "0 8px",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#F7F7F7",
    cursor: "pointer",
    borderLeft: "2px solid #D1C4E9",
    borderRight: "2px solid #D1C4E9",
  },
}));
const columnFlex = {
  sr: 0.6,
  event: 1.2,
  patientId: 1.4,
  patientName: 2,
  phone: 1.5,
  action: 1,
};

const LongTermReport = () => {
  const accessToken = localStorage.getItem("token");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [filterType, setFilterType] = useState("searchby");
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [tempSearchValue, setTempSearchValue] = useState("");

  const [loading, setLoading] = useState(false);

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [tabledata, setTableData] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loadingRows, setLoadingRows] = useState({});

  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(min-width:601px) and (max-width:900px)");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // can be 'error', 'info', 'warning'
  });

  const reportRef = useRef(null);

  const handleDateChange = ([start, end]) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleFilterTypeChange = (e) => {
    setFilterType(e.target.value);
    setShowSearchBox(true);
  };

  const handleTempSearchChange = (e) => {
    setTempSearchValue(e.target.value);
  };

const handleViewDetails = async (row) => {
  if (!row?.event_id) return;

  // row-wise loading
  setLoadingRows((prev) => ({ ...prev, [row.event_id]: true }));

  try {
    const response = await fetch(`${API_URL}/web/long_term_get_idwise_pdf/${row.event_id}/`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const result = await response.json();
    const report = result && Object.keys(result).length > 0 ? result : null;
    setReportData(report);
    setOpenModal(true);
  } catch (error) {
    console.error("Error fetching report:", error);
    setReportData(null);
  } finally {
    setLoadingRows((prev) => ({ ...prev, [row.event_id]: false }));
  }
};

  const getPlaceholderText = () => {
    switch (filterType) {
      case "patient_name":
        return "Enter Patient Name";
      case "phone_no":
        return "Enter Phone Number";
      case "hhc_id":
        return "Enter HHC ID";
      default:
        return "Search";
    }
  };

  const filterMap = {
    patient_name: 1,
    phone_no: 2,
    hhc_id: 3,
  };

  const handleSubmit = async () => {
    if (filterType === "searchby" || !tempSearchValue) return;

    try {
      setLoading(true);

      const filterBy = filterMap[filterType];
      const filterValue = tempSearchValue.trim();

      const response = await fetch(
        `${API_URL}/hhc_admin/long_term_report_filter/${filterBy}/${filterValue}/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      const result = await response.json();
      setTableData(Array.isArray(result) ? result : result.data || []);
      setPage(0);
    } catch (error) {
      console.error("Filter error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterType("searchby");
    setShowSearchBox(false);
    setTempSearchValue("");
    fetchFormData(); // 🔥 reload full table
  };

  const filteredData = useMemo(
    () => tabledata.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [tabledata, page, rowsPerPage]
  );

  const fetchFormData = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${API_URL}/hhc_admin/long_term_report_get/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const result = await response.json();
      setTableData(Array.isArray(result) ? result : result.data || []);
    } catch (error) {
      console.error("Table fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormData();
  }, []);

 

  return (
    <>
      <HRNavbar />
      <Box sx={{ flexGrow: 1, mt: 11, ml: 1, mr: 1 }}>
        <Stack
          direction={isSmallScreen || isTablet ? "column" : "row"}
          spacing={2}
          alignItems={isSmallScreen || isTablet ? "center" : "flex-start"}
          justifyContent={isSmallScreen || isTablet ? "center" : "flex-start"}
          flexWrap="wrap"
          useFlexGap
          sx={{
            pt: 2,
            textAlign: isSmallScreen || isTablet ? "center" : "left",
          }}
        >
          {/* Title */}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="text.secondary"
            sx={{ mt: isSmallScreen ? 1 : "5px" }}
          >
            Long Term Report
          </Typography>

          {/* Date Picker */}
          {/* <Box
            sx={{
              width: 250,
              // bgcolor: "#ffffff",
              boxShadow: "4px 4px 10px 7px rgba(135, 135, 135, 0.05)",
              borderRadius: "10px",
              // border: "1px solid #C9C9C9",
            }}
          >
            <DatePicker
              range
              value={[startDate, endDate]}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
              render={(value, openCalendar) => (
                <TextField
                  onClick={openCalendar}
                  label="Select Date Range"
                  size="small"
                  fullWidth
                  sx={{
                    height: 39,
                    boxShadow: "4px 4px 10px 7px rgba(135, 135, 135, 0.05)",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    mb: 1,
                    width: 250,
                  }}
                  value={
                    startDate && endDate
                      ? `${startDate.format("YYYY-MM-DD")} - ${endDate.format(
                          "YYYY-MM-DD"
                        )}`
                      : ""
                  }
                />
              )}
            />
          </Box> */}

          {/* Filter Type Dropdown */}
          <Box
            sx={{
              width: 250,
              bgcolor: "#ffffff",
              boxShadow: "4px 4px 10px 7px rgba(135, 135, 135, 0.05)",
              borderRadius: "10px",
              border: "1px solid #C9C9C9",
            }}
          >
            <TextField
              select
              size="small"
              fullWidth
              value={filterType}
              onChange={handleFilterTypeChange}
            >
              <MenuItem value="searchby" disabled>
                Search by
              </MenuItem>
              <MenuItem value="patient_name">Patient Name</MenuItem>
              <MenuItem value="phone_no">Phone Number</MenuItem>
              <MenuItem value="hhc_id">HHC ID</MenuItem>
            </TextField>
          </Box>

          {/* Conditional Search Box */}
          {showSearchBox && (
            <Box
              sx={{
                width: 250,
                bgcolor: "#ffffff",
                display: "flex",
                alignItems: "center",
                boxShadow: "4px 4px 10px 7px rgba(135, 135, 135, 0.05)",
                borderRadius: "10px",
                border: "1px solid #C9C9C9",
              }}
            >
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder={getPlaceholderText()}
                value={tempSearchValue}
                onChange={handleTempSearchChange}
              />
              <IconButton type="button">
                <SearchIcon style={{ color: "#7AB7EE" }} />
              </IconButton>
            </Box>
          )}

          {/* Submit and Reset Buttons */}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            style={{
              backgroundColor: "#347B89",
              color: "white",
              textTransform: "capitalize",
            }}
          >
            view
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            style={{ color: "#347B89", textTransform: "capitalize" }}
          >
            Reset
          </Button>
          {/* <Tooltip title="Download Report" arrow>
                <IconButton
                  onClick={handleDownload}
                  color="primary"
                  aria-label="Download"
                  style={{ color: '#347B89', textTransform: 'capitalize' }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip> */}
        </Stack>

        <TableContainer sx={{ height: tabledata.length < 5 ? "60vh" : "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell colSpan={7} sx={{ border: "none", p: 0 }}>
                  <LongtermFormcardHader sx={{ backgroundColor: "#D1C4E9" }}>
                    <CardContent
                      sx={{
                        flex: columnFlex.sr,
                        textAlign: "center",
                        borderRight: "1px solid #FFFFFF",
                        p: 1,
                      }}
                    >
                      Sr. No
                    </CardContent>
                    <CardContent
                      sx={{
                        flex: columnFlex.event,
                        textAlign: "center",
                        borderRight: "1px solid #FFFFFF",
                        p: 1,
                      }}
                    >
                      Event ID
                    </CardContent>
                    <CardContent
                      sx={{
                        flex: columnFlex.patientId,
                        textAlign: "center",
                        borderRight: "1px solid #FFFFFF",
                        p: 1,
                      }}
                    >
                      Patient ID
                    </CardContent>
                    <CardContent
                      sx={{
                        flex: columnFlex.patientName,
                        textAlign: "center",
                        borderRight: "1px solid #FFFFFF",
                        p: 1,
                      }}
                    >
                      Patient Name
                    </CardContent>
                    <CardContent
                      sx={{
                        flex: columnFlex.phone,
                        textAlign: "center",
                        borderRight: "1px solid #FFFFFF",
                        p: 1,
                      }}
                    >
                      Phone No
                    </CardContent>
                    <CardContent
                      sx={{
                        flex: columnFlex.action,
                        textAlign: "center",
                        p: 1,
                      }}
                    >
                      Action
                    </CardContent>
                  </LongtermFormcardHader>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{ border: "none", p: 3, textAlign: "center" }}
                  >
                    <CircularProgress size={30} thickness={4} color="primary" />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Loading feedback...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{ border: "none", p: 3, textAlign: "center" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No feedback data available.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={7} sx={{ border: "none", p: 0 }}>
                      <LongtermFormcard>
                        <CardContent
                          sx={{ flex: columnFlex.sr, textAlign: "center" }}
                        >
                          {page * rowsPerPage + index + 1}
                        </CardContent>

                        <CardContent
                          sx={{ flex: columnFlex.event, textAlign: "center" }}
                        >
                          {row.event_id}
                        </CardContent>

                        <CardContent
                          sx={{
                            flex: columnFlex.patientId,
                            textAlign: "center",
                          }}
                        >
                          {row.patient_id}
                        </CardContent>

                        <CardContent
                          sx={{
                            flex: columnFlex.patientName,
                            textAlign: "center",
                          }}
                        >
                          {row.patient_name}
                        </CardContent>

                        <CardContent
                          sx={{ flex: columnFlex.phone, textAlign: "center" }}
                        >
                          {row.patient_phone}
                        </CardContent>

                        <CardContent
  sx={{
    flex: columnFlex.action,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  }}
>
  <Button
    size="small"
    variant="contained"
    disabled={!!loadingRows[row.event_id]}
    onClick={() => handleViewDetails(row)}
  >
    {loadingRows[row.event_id] ? "Loading..." : "View"}
  </Button>
</CardContent>
                      </LongtermFormcard>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          sx={{ overflowY: "hidden" }}
          rowsPerPageOptions={[5, 25, 50]}
          component="div"
          count={tabledata.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Box>

      <Footer />

      <LongTermReportModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        loading={detailLoading}
        reportData={reportData}
      />
    </>
  );
};

export default LongTermReport;
